/**
 * Снимок экрана для «Сообщить об ошибке» — чистая часть без React (образец —
 * frontend/src/components/feedback/feedback-ui.tsx: captureScreen / canCaptureScreen /
 * ScreenshotAnnotator; импорт между пакетами запрещён, поэтому перенесено сюда).
 * Рисование рамок — components/feedback/ScreenshotAnnotator.tsx.
 */

/** Рамка на снимке в пикселях самой картинки (не экрана). */
export type ShotRect = { x: number; y: number; w: number; h: number };

/** PNG тяжелее этого отдаём JPEG: сервер принимает файл до 10 МБ, 2 МБ — запас на запрос. */
export const SHOT_PNG_MAX_BYTES = 8 * 1024 * 1024;
/** Качество JPEG, когда PNG не поместился. */
export const SHOT_JPEG_QUALITY = 0.85;
/** Рамка короче этого (в пикселях картинки) — случайный клик, а не обводка. */
export const SHOT_MIN_SIDE = 6;

/** В каком формате сохранять снимок: PNG, пока он не тяжелее лимита, иначе JPEG. */
export function shotFormatBySize(pngBytes: number): 'png' | 'jpeg' {
  return pngBytes > SHOT_PNG_MAX_BYTES ? 'jpeg' : 'png';
}

/** Имя файла снимка по его типу (так обращение видно на доске CRM как «снимок-экрана»). */
export function shotFileName(mime: string): string {
  return mime === 'image/jpeg' ? 'снимок-экрана.jpg' : 'снимок-экрана.png';
}

/** Рамка по двум углам, протянутым в любую сторону. */
export function normRect(a: { x: number; y: number }, b: { x: number; y: number }): ShotRect {
  return { x: Math.min(a.x, b.x), y: Math.min(a.y, b.y), w: Math.abs(a.x - b.x), h: Math.abs(a.y - b.y) };
}

/** Рамка достаточно большая, чтобы считаться обводкой. */
export const isShotRect = (r: ShotRect): boolean => r.w >= SHOT_MIN_SIDE && r.h >= SHOT_MIN_SIDE;

/** Толщина линии: на снимке 4K линия толще, чтобы рамку было видно и в уменьшенном виде. */
export const shotLineWidth = (imageWidth: number): number => Math.max(3, Math.round(imageWidth / 400));

type DisplayMedia = MediaDevices & { getDisplayMedia?: (c?: unknown) => Promise<MediaStream> };

/** Умеет ли браузер сделать снимок вкладки (на телефонах getDisplayMedia нет — там только загрузка файла). */
export const canCaptureScreen = (): boolean =>
  typeof navigator !== 'undefined' && typeof (navigator.mediaDevices as DisplayMedia | undefined)?.getDisplayMedia === 'function';

/**
 * Снимок текущей вкладки без сторонних библиотек: getDisplayMedia (браузер
 * спросит разрешение «поделиться этой вкладкой»), один кадр → PNG data-URL.
 * null — пользователь отказался или браузер не умеет (тогда — загрузка файла).
 */
export async function captureScreen(): Promise<string | null> {
  if (!canCaptureScreen()) return null;
  const md = navigator.mediaDevices as DisplayMedia;
  let stream: MediaStream | null = null;
  let video: HTMLVideoElement | null = null;
  try {
    stream = await md.getDisplayMedia!({ video: { displaySurface: 'browser' }, audio: false, preferCurrentTab: true, selfBrowserSurface: 'include' });
    video = document.createElement('video');
    video.srcObject = stream;
    video.muted = true;
    await video.play();
    // Пара кадров — чтобы браузер успел убрать свою плашку «идёт показ».
    await new Promise((r) => setTimeout(r, 350));
    if (!video.videoWidth || !video.videoHeight) return null;
    const c = document.createElement('canvas');
    c.width = video.videoWidth;
    c.height = video.videoHeight;
    c.getContext('2d')?.drawImage(video, 0, 0);
    return c.toDataURL('image/png');
  } catch {
    return null;
  } finally {
    stream?.getTracks().forEach((t) => t.stop());
    if (video) video.srcObject = null;
  }
}
